import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, X, Link2, Check } from "lucide-react";
import { toast } from "sonner";

interface LinkedRecordPickerProps {
  open: boolean;
  onClose: () => void;
  targetTableId: string;
  fieldId: string;
  sourceRecordId: string;
  selectedRecordIds: string[];
  onSelect: (recordIds: string[]) => void;
  allowMultiple?: boolean;
}

export const LinkedRecordPicker = ({
  open,
  onClose,
  targetTableId,
  fieldId,
  sourceRecordId,
  selectedRecordIds,
  onSelect,
  allowMultiple = true,
}: LinkedRecordPickerProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [records, setRecords] = useState<any[]>([]);
  const [fields, setFields] = useState<any[]>([]);
  const [targetTable, setTargetTable] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string[]>(selectedRecordIds);

  useEffect(() => {
    if (open && targetTableId) {
      fetchTargetData();
    }
  }, [open, targetTableId]);

  useEffect(() => {
    setSelected(selectedRecordIds);
  }, [selectedRecordIds]);

  const fetchTargetData = async () => {
    setLoading(true);
    try {
      // Fetch target table info
      const { data: tableData, error: tableError } = await supabase
        .from("tables")
        .select("*")
        .eq("id", targetTableId)
        .single();

      if (tableError) throw tableError;
      setTargetTable(tableData);

      // Fetch fields for the target table
      const { data: fieldsData, error: fieldsError } = await supabase
        .from("fields")
        .select("*")
        .eq("table_id", targetTableId)
        .order("order_index");

      if (fieldsError) throw fieldsError;
      setFields(fieldsData || []);

      // Fetch records from target table
      const { data: recordsData, error: recordsError } = await supabase
        .from("records")
        .select("*")
        .eq("table_id", targetTableId)
        .order("created_at", { ascending: false })
        .limit(100);

      if (recordsError) throw recordsError;
      setRecords(recordsData || []);
    } catch (error: any) {
      console.error("Failed to fetch target data:", error);
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  const getPrimaryFieldValue = (record: any) => {
    // Use first text field as primary display field
    const primaryField = fields.find(
      (f) => f.type === "text" || f.type === "long_text"
    );
    if (primaryField && record.data) {
      return record.data[primaryField.name] || `Record ${record.id.slice(0, 8)}`;
    }
    return `Record ${record.id.slice(0, 8)}`;
  };

  const filteredRecords = records.filter((record) => {
    if (!searchQuery) return true;
    const primaryValue = getPrimaryFieldValue(record);
    return primaryValue.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const toggleRecord = (recordId: string) => {
    if (allowMultiple) {
      setSelected((prev) =>
        prev.includes(recordId)
          ? prev.filter((id) => id !== recordId)
          : [...prev, recordId]
      );
    } else {
      setSelected([recordId]);
    }
  };

  const handleConfirm = () => {
    onSelect(selected);
    onClose();
  };

  const handleRemove = (recordId: string) => {
    setSelected((prev) => prev.filter((id) => id !== recordId));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Link to {targetTable?.name || "Records"}
          </DialogTitle>
        </DialogHeader>

        {/* Selected records */}
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 bg-muted/50 rounded-lg">
            {selected.map((id) => {
              const record = records.find((r) => r.id === id);
              return (
                <Badge key={id} variant="secondary" className="gap-1 pr-1">
                  {record ? getPrimaryFieldValue(record) : id.slice(0, 8)}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-destructive/20"
                    onClick={() => handleRemove(id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              );
            })}
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search records..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Records list */}
        <ScrollArea className="flex-1 max-h-[400px]">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">
              Loading records...
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {searchQuery ? "No matching records" : "No records in this table"}
            </div>
          ) : (
            <div className="space-y-1 p-1">
              {filteredRecords.map((record) => {
                const isSelected = selected.includes(record.id);
                const primaryValue = getPrimaryFieldValue(record);
                
                return (
                  <div
                    key={record.id}
                    className={`
                      flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors
                      ${isSelected 
                        ? "bg-primary/10 border border-primary/30" 
                        : "hover:bg-muted border border-transparent"
                      }
                    `}
                    onClick={() => toggleRecord(record.id)}
                  >
                    <div className={`
                      h-5 w-5 rounded border-2 flex items-center justify-center transition-colors
                      ${isSelected 
                        ? "bg-primary border-primary text-primary-foreground" 
                        : "border-muted-foreground/30"
                      }
                    `}>
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{primaryValue}</div>
                      <div className="text-xs text-muted-foreground">
                        {Object.entries(record.data || {})
                          .slice(0, 2)
                          .filter(([key]) => key !== fields[0]?.name)
                          .map(([key, val]) => `${key}: ${val}`)
                          .join(" • ")}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <span className="text-sm text-muted-foreground self-center">
              {selected.length} selected
            </span>
            <Button onClick={handleConfirm}>
              Link {selected.length > 0 ? `(${selected.length})` : ""}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
